import { inject, Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { timeout } from 'rxjs';
import { WorkoutService } from '@app-training/data-access/infra/workout.service';
import { IWorkoutItem } from '@app-training/data-access/entities/workouts.interface';
import { createAppStateTransitionController } from '@shared/utils/app-state-transition-controller.util';
import { isUnansweredSeriesSave, SERIES_SAVE_TIMEOUT_MS } from '@app-training/data-access/series-save.util';

export interface PendingSeriesSave {
  workoutItem: IWorkoutItem;
  seriesPerformed: number;
  previousSeriesPerformed: number;
  attempts: number;
  enqueuedAt: number;
}

/**
 * Backstop cadence: while entries are queued, retry the flush on this interval,
 * so delivery no longer depends on a network-state transition that the common
 * "connected but no throughput" outage never emits.
 */
export const SERIES_SAVE_BACKSTOP_MS = 30_000;

/**
 * Fila em memória das gravações de série que o retry inline não conseguiu
 * entregar num outage sustentado. Chave por TrainingItemId, último-vence, porque
 * a gravação é absoluta. Descarrega na reconexão de rede e no retorno do app.
 * Não persiste: matar o app perde a fila, limite aceite da opção em memória.
 */
@Injectable({ providedIn: 'root' })
export class SeriesSaveOutbox {
  private readonly workoutService = inject(WorkoutService);
  private readonly controller = createAppStateTransitionController();
  private readonly pending = new Map<string, PendingSeriesSave>();
  private onGaveUp: ((entry: PendingSeriesSave, error: unknown) => void) | null = null;
  private isFlushing = false;
  private backstopTimer: ReturnType<typeof setInterval> | undefined;
  private started = false;
  private isAppActive = true;

  public size(): number {
    return this.pending.size;
  }

  public has(trainingItemId: string): boolean {
    return this.pending.has(trainingItemId);
  }

  /** Lê a pendente sem a remover, para uma escrita nova herdar o seu prev salvo. */
  public peek(trainingItemId: string): PendingSeriesSave | undefined {
    return this.pending.get(trainingItemId);
  }

  public enqueue(entry: PendingSeriesSave): void {
    this.pending.set(entry.workoutItem.TrainingItemId, entry);
    this.startBackstop();
  }

  public discard(trainingItemId: string): void {
    this.pending.delete(trainingItemId);
    if (this.pending.size === 0) this.stopBackstop();
  }

  public start(handlers: { onGaveUp: (entry: PendingSeriesSave, error: unknown) => void }): void {
    this.onGaveUp = handlers.onGaveUp;
    if (this.started) return;
    this.started = true;

    Network.addListener('networkStatusChange', status => {
      // Em background a ligação fica fechada de propósito; sem este guard um
      // evento de rede reabriria e ainda dispararia requisição. Espelha o chat.
      if (this.isAppActive && status.connected) {
        this.controller.schedule(() => this.flush());
      }
    });

    App.addListener('appStateChange', ({ isActive }) => {
      this.isAppActive = isActive;
      if (isActive) {
        this.controller.schedule(() => this.flush(), 250);
      }
    });
  }

  public flush(): void {
    if (this.pending.size === 0) {
      this.stopBackstop();
      return;
    }
    if (this.isFlushing) return;
    this.isFlushing = true;

    const entries = [...this.pending.values()];
    let remaining = entries.length;
    const settle = (): void => {
      remaining -= 1;
      if (remaining > 0) return;
      this.isFlushing = false;
      // Nothing left to deliver: drop the periodic retry until the next enqueue.
      if (this.pending.size === 0) this.stopBackstop();
    };

    for (const entry of entries) {
      const key = entry.workoutItem.TrainingItemId;

      this.workoutService
        .updateSeriesPerformed(key, entry.seriesPerformed)
        .pipe(timeout({ first: SERIES_SAVE_TIMEOUT_MS }))
        .subscribe({
          next: () => {
            this.pending.delete(key);
            settle();
          },
          error: error => {
            if (isUnansweredSeriesSave(error)) {
              entry.attempts += 1; // keep it queued for the next trigger
            } else {
              this.pending.delete(key);
              this.onGaveUp?.(entry, error);
            }
            settle();
          },
        });
    }
  }

  private startBackstop(): void {
    if (this.backstopTimer) return;
    this.backstopTimer = setInterval(() => this.flush(), SERIES_SAVE_BACKSTOP_MS);
  }

  private stopBackstop(): void {
    if (!this.backstopTimer) return;
    clearInterval(this.backstopTimer);
    this.backstopTimer = undefined;
  }
}
